import { filter, map, tap } from 'rxjs/operators';
import { where } from '@angular/fire/firestore';
import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { Profile, ProfileStatus } from './profile.model';
import { UserService } from 'src/app/user/user.service';
import { ProfileFirestoreService } from 'src/app/database/firestore/profile-firestore.service';
import { ProfileStoreService } from 'src/app/database/store/profile-store.service';
import {
  ActionFilterClass,
  MappingsServiceSettings,
  MovementSource,
} from 'src/app/nav-processing/mappings/mappings.model';
import { AuthService } from '../auth/auth.service';
import { VoiceServiceSettings } from '../voice/voice.model';
import { NavigationCursorServiceSettings } from 'src/app/nav-overlay/navigation-cursor.model';
import { AndroidControlService } from '../androidControl/androidControl.service';
import { environment } from 'src/environments/environment';
import {
  GeneralSettingsState,
  ItemID,
  KeyboardLayouts,
  NavSettingsBundle,
} from '../manual-settings/manual-settings.model';
import {
  bundleHead2DHV,
  bundleTobii,
  bundleEyeTrackerColibri,
  bundleHead1DHMouth1DV,
  bundleHead1DH,
  bundleMouth1D,
  bundleScan,
  bundleTouch,
  profilePresets,
} from '../manual-settings/manual-settings.data';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  userSubscription: Subscription;
  profileListSubscription: Subscription;
  listOfProfiles: Profile[];
  uid;

  activeProfileIdSub: Subscription; // Subscribes to user doc active profile ID
  activeProfileSub: Subscription; // Subscribes to active profile doc
  activeProfileId: string;

  profilePresets: Array<Profile>;

  private lastCloudProfile: string;
  private autoSyncInterval;

  constructor(
    private user: UserService,
    private profileStore: ProfileStoreService,
    private profileFirestore: ProfileFirestoreService,
    private auth: AuthService,
    private androidControl: AndroidControlService
  ) {
    this.profileStore.clear('clear before default');
    this.profileStore.set(this.defaultProfile(), 'set default');

    this.userSubscription = user.uid$.subscribe((uid) => {
      if (uid) {
        profileFirestore.basePath = 'users/' + uid + '/profiles';
        this.uid = uid;
        console.log('[Profile][basePath]', profileFirestore.basePath);

        this.setupActiveProfileListener();
        this.setupProfileListener();
      } else {
        //this.profileStore.clear('clear after logout');
        this.activeProfileIdSub?.unsubscribe();
        this.activeProfileSub?.unsubscribe();
        this.profileListSubscription?.unsubscribe();
        this.profileStore.set(
          this.defaultProfile(),
          'set default user not logged in'
        );
      }
    });

    // Propagação de alterações para o Android Nativo
    // this.profileStore.state$.subscribe((state) => {
    //   if(state.mappingsServiceSettings) {
    //     console.log( 'mappingsServiceSettings', state.mappingsServiceSettings.toString)
    //     this.androidControl.receiveConfig(state.mappingsServiceSettings.mappings);
    //   }

    //   //
    // });
  }

  get mappingsSettings$() {
    return this.profileStore.state$
      .pipe(filter((val) => val?.mappingsServiceSettings !== undefined))
      .pipe(map((state) => state.mappingsServiceSettings));
  }

  get voiceSettings$() {
    return this.profileStore.state$
      .pipe(filter((val) => val?.voiceServiceSettings !== undefined))
      .pipe(map((state) => state.voiceServiceSettings));
  }

  get mappingsSettings() {
    return this.profileStore.state.mappingsServiceSettings;
  }

  set mappingsSettings(mappingsServiceSettings: MappingsServiceSettings) {
    this.profileStore.patch(
      {
        mappingsServiceSettings,
      },
      'profile set mappingsSettings'
    );
  }

  get voiceSettings() {
    return this.profileStore.state.voiceServiceSettings;
  }

  set voiceSettings(voiceServiceSettings: VoiceServiceSettings) {
    this.profileStore.patch(
      {
        voiceServiceSettings,
      },
      'profile set voiceSettings'
    );
  }

  get generalSettingsState$() {
    return this.profileStore.state$
      .pipe(filter((val) => val?.generalSettingsState !== undefined))
      .pipe(map((state) => state.generalSettingsState));
  }

  set generalSettingsState(generalSettingsState: GeneralSettingsState) {
    this.profileStore.patch(
      {
        generalSettingsState,
      },
      'profile set generalSettingsState'
    );
  }

  get generalSettingsState() {
    return this.profileStore.state.generalSettingsState;
  }

  get navigationCursorSettings$() {
    return this.profileStore.state$
      .pipe(filter((val) => val?.navigationCursorSettings !== undefined))
      .pipe(map((state) => state.navigationCursorSettings));
  }

  set navigationCursorSettings(
    navigationCursorSettings: NavigationCursorServiceSettings
  ) {
    this.profileStore.patch(
      {
        navigationCursorSettings,
      },
      'profile set navigationCursorSettings'
    );
  }

  get navigationCursorSettings() {
    return this.profileStore.state.navigationCursorSettings;
  }

  get navSettingsBundle$() {
    return this.profileStore.state$
      .pipe(filter((val) => val?.navSettingsBundle !== undefined))
      .pipe(map((state) => state.navSettingsBundle));
  }

  set navSettingsBundle(navSettingsBundle: NavSettingsBundle) {
    console.log('navSettingsBundle', navSettingsBundle);
    this.profileStore.patch(
      {
        navSettingsBundle,
      },
      'profile set navSettingsBundle'
    );
  }

  get profile() {
    return this.profileStore.state || this.defaultProfile();
  }

  get profile$() {
    return this.profileStore.state$.pipe(filter((val) => val !== undefined));
  }

  set profile(profile: Profile) {
    this.profileStore.patch(profile, 'profile set profile');
  }

  get navSettingsBundle() {
    return this.profileStore.state.navSettingsBundle;
  }

  async commit() {
    if (this.auth.isLoggedIn && this.user.uid) {
      this.profileStore.patch(
        { ownerUid: this.uid, version: this.profileStore.state.version + 1 },
        'commit'
      );
      if (this.activeProfileId === 'default') {
        // Profile does not exist on the cloud
        await this.profileFirestore.create(this.profileStore.state).then(
          (ref) => {
            console.log('onSaveProfile create', ref);
            this.user.setActiveProfile(ref, true).then(() => {
              this.lastCloudProfile = JSON.stringify(this.profileStore.state);
            });
          },
          (err) => {
            console.error('onSaveProfile', err);
          }
        );
      } else {
        // Profile already exists on the cloud
        this.profileFirestore
          .update(this.profileStore.state, this.activeProfileId)
          .then(() => {
            this.lastCloudProfile = JSON.stringify(this.profileStore.state);
          });
      }
    }
  }

  createOnCloud(profile: Profile) {
    this.profileFirestore.create(profile).then(
      (ref) => {
        //console.log('onSaveProfile create', ref);
        this.user.setActiveProfile(ref, true);
      },
      (err) => {
        console.log('createOnCloud', err);
      }
    );
  }

  /**
   * Enable or disable the autoSync feature
   * @param enable
   */
  autoSync(enable: boolean) {
    clearInterval(this.autoSyncInterval);
    if (this.user.uid && enable) {
      this.autoSyncInterval = setInterval(() => this.autoSyncCallback(), 2000);
    }
  }

  autoSyncCallback() {
    //console.log('autoSyncCallback');
    if (this.user.uid && this.changeSinceLastCloudSync()) {
      this.commit();
    }
  }

  export(profile: Profile) {
    console.log('[Profile EXPORT]');
    const file = JSON.stringify(profile);
    this.download(profile.name + ' (' + profile.docId + ')', file);
  }

  import(ev: any) {
    const file = ev.target.files[0];
    console.log('[Profile IMPORT]', ev, file);
    let reader = new FileReader();

    // Is this a "real" file? In other words, is this an instance of the original `File` class
    // (not the one overriden by cordova-plugin-file). If so, then we need to use the "real"
    // FileReader (not the one overriden by cordova-plugin-file).
    if (file instanceof Blob) {
      // eslint-disable-next-line no-underscore-dangle
      const realFileReader = (reader as any)._realReader;
      if (realFileReader) {
        reader = realFileReader;
      }
    }

    reader.onload = (e) => {
      const parsedFile = JSON.parse(reader.result.toString());
      console.log('[DADO IMPORT]', parsedFile);
      //this.createOnCloud(parsedFile.data);
    };

    reader.onerror = (error) => {
      console.log(error);
    };

    reader.readAsText(file);
  }

  resetProfileToDefaults() {
    //this.profileStore.clear('clear resetProfileToDefaults');
    this.profileStore.set(
      this.defaultProfile(),
      'set default after resetProfileToDefaults'
    );
  }

  createProfile(preset: Profile) {
    // Profile does not exist on the cloud
    preset.ownerUid = this.uid;
    return this.profileFirestore.create(preset).then(
      (ref) => {
        console.log('onSaveProfile create', ref);
        this.user.setActiveProfile(ref, true).then(() => {
          this.lastCloudProfile = JSON.stringify(this.profileStore.state);
        });
      },
      (err) => {
        console.log('onSaveProfile', err);
      }
    );
  }

  removeProfile(id: string) {
    return this.profileFirestore.delete(id);
  }

  /**
   * Listens for the list of available profiles from the collection /users/{uid}/profiles
   * and updates the local list considering only the profiles
   * where status === 'available'
   *
   * @param status ProfileStatus filter
   * @returns void
   *
   */
  private setupProfileListener(
    status: ProfileStatus = ProfileStatus.Available
  ) {
    this.profileListSubscription?.unsubscribe();
    // Use Firestore modular query constraints
    this.profileListSubscription = this.profileFirestore
      .collection$([where('status', '==', status)])
      .pipe(
        tap((data) => {
          this.listOfProfiles = data;
          //console.log('listOfProfiles', this.listOfProfiles);
        })
      )
      .subscribe();
  }

  private setupActiveProfileListener() {
    this.activeProfileIdSub?.unsubscribe();
    this.activeProfileIdSub = this.user.activeProfile$.subscribe((value) => {
      //console.log('activeProfile', value);
      let profileChanged;
      if (this.activeProfileId !== value) {
        profileChanged = true;
        console.log('activeProfile changed', this.activeProfileId, value);
      } else {
        console.log('activeProfile unchanged', this.activeProfileId, value);
        profileChanged = false;
      }
      if (value !== 'default') {
        this.activeProfileId = value;
        this.activeProfileSub?.unsubscribe();
        this.activeProfileSub = this.profileFirestore
          .doc$(value)
          .subscribe((data) => {
            //console.log('activeProfileData', data);
            // Check if profile is valid
            if (!this.validateProfile(data)) {
              //this.createProfile(this.defaultProfile());
              this.activeProfileId = 'default';
              this.profileStore.patch(
                this.defaultProfile(),
                'set default profile - invalid setting'
              );
              return;
            }
            // If profile is valid, patch the profile store
            if (
              data.docId !== this.profileStore.state.docId
            ) {
              this.profileStore.set(data, `profile doc subscription 1`);
              this.lastCloudProfile = JSON.stringify(this.profileStore.state);
            } else if (data?.version > this.profileStore.state.version) {
              this.profileStore.patch(data, `profile doc subscription 2`);
              this.lastCloudProfile = JSON.stringify(this.profileStore.state);
            }
          });
      } else {
        this.activeProfileId = 'default';
        //this.activeProfileSub?.unsubscribe();
        //this.profileStore.clear('clear before creation');
        this.profileStore.patch(this.defaultProfile(), 'set default first use');
      }
    });
  }

  private download(filename, text) {
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/plain;charset=utf-8,' + encodeURIComponent(text)
    );
    element.setAttribute('download', filename + '.cprofile');

    //Criação de um btn invisível para realizar a ação de download
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  private defaultProfile(): Profile {
    // When running in "link" app type, use the webcam gaze tracker profile as default
    try {
      if ((environment as any).appType === 'link') {
        const gazePreset = profilePresets.find(p => p.name === 'Rastreador Ocular WebCam');
        if (gazePreset) {
          return gazePreset;
        }
      }
    } catch (e) {
      // fallback to standard default
    }
    return profilePresets[0];
  }

  private changeSinceLastCloudSync(
    value: Profile = this.profileStore.state
  ): boolean {
    // console.log('changeSinceLastCloudSync', this.lastCloudProfile !== JSON.stringify(value),
    //   this.lastCloudProfile, '||||||||||||||', JSON.stringify(value));
    const compare = this.lastCloudProfile !== JSON.stringify(value);

    if (compare) {
      console.groupCollapsed(
        'changeSinceLastCloudSync',
        this.lastCloudProfile !== JSON.stringify(value)
      );
      console.log('change', value);
      console.log('prev', this.lastCloudProfile);
      console.log('next', JSON.stringify(value));
      console.groupEnd();
    }
    return compare;
  }

  private sortedProfile(unordered): Profile {
    return Object.keys(unordered)
      .sort()
      .reduce((obj, key) => {
        obj[key] = unordered[key];
        return obj;
      }, {}) as Profile;
  }

  /**
   *  Upgrades the navSettingsBundle to the latest version
   *  based on the bundle id and version
   */
  navSettingsBundleUpgrade(bundle: NavSettingsBundle): NavSettingsBundle {
    let newVersion: NavSettingsBundle;
    switch (bundle.id) {
      case ItemID.NavBundleEyeTrackerPeye:
        newVersion = bundleEyeTrackerColibri;
        break;
      case ItemID.NavBundleTobii:
        newVersion = bundleTobii;
        break;
      case ItemID.NavBundleHead2DHV:
        newVersion = bundleHead2DHV;
        break;
      case ItemID.NavBundleHead1DHMouth1DV:
        newVersion = bundleHead1DHMouth1DV;
        break;
      case ItemID.NavBundleHead1DH:
        newVersion = bundleHead1DH;
        break;
      case ItemID.NavBundleMouth1D:
        newVersion = bundleMouth1D;
        break;
      case ItemID.NavBundleScan:
        newVersion = bundleScan;
        break;
      case ItemID.NavBundleTouch:
        newVersion = bundleTouch;
        break;
      default:
        console.log('navSettingsBundleUpgrader', bundle.id, 'not upgradable');
        return bundle;
    }

    // Check if new version is greater than the current version
    if (bundle.version >= newVersion.version) {
      //console.log('navSettingsBundleUpgrader', bundle, 'is already up to date');
      return bundle;
    }

    // Copy the bundleOptionsState from the old bundle to the new one
    newVersion.bundleOptionsState = bundle.bundleOptionsState;
    // Copy the directions.mappings from the old bundle to the new one
    bundle.directions.forEach((direction) => {
      const newDirection = newVersion.directions.find(
        (d) => d.id === direction.id
      );
      if (newDirection) {
        newDirection.mappings = direction.mappings;
      }
    });
    // Copy the switches.mappings from the old bundle to the new one
    bundle.switches.forEach((sw) => {
      const newSwitch = newVersion.switches.find((s) => s.id === sw.id);
      if (newSwitch) {
        newSwitch.mappings = sw.mappings;
      }
    });
    // Copy switches that are not present in the new bundle
    bundle.switches.forEach((sw) => {
      // Chech if the switch is present in the new bundle default switches
      const newSwitch = newVersion.switches.find((s) => s.id === sw.id);
      if (!newSwitch) {
        // if not get the equivalent switch from the new bundle available switch list
        const newSwitchAvailable = newVersion.switches.find(
          (s) => s.id === sw.id
        );
        if (newSwitchAvailable) {
          newVersion.switches.push(newSwitchAvailable);
        } else {
          // if no correspondence found use old switch
          newVersion.switches.push(sw);
        }
      }
    });

    console.log('navSettingsBundleUpgrader', bundle, 'upgraded to', newVersion);
    return newVersion;
  }

  validateProfile(profile: Profile): boolean {
    // Check if the profile navSettingsBundle is valid
    if (!profile.navSettingsBundle) {
      console.error('validateProfile navSettingsBundle is missing', profile);
      return false;
    }

    // Check if the profile directions mappings length is greater or equal to settings length
    profile.navSettingsBundle.directions.forEach((direction) => {
      if (direction.mappings.length < direction.options?.length) {
        console.error(
          'validateProfile',
          'directions',
          direction.id,
          'mappings and settings length mismatch'
        );
        return false;
      }
    });

    // Check if the profile switches mappings length is greater or equal to settings length
    profile.navSettingsBundle.switches.forEach((sw) => {
      if (sw.mappings.length < sw.options.length) {
        console.error(
          'validateProfile',
          'switches',
          sw.id,
          'mappings and settings length mismatch'
        );
        return false;
      }
    });

    return true;
  }
}
